<template>
  <div class="patternList">
    <table class="patternList__container">
      <thead class="patternList__thead">
        <tr>
          <th>{{ $t('pages.companies.services.table.header.servicePatternName') }}</th>
          <th>{{ $t('pages.companies.services.table.header.services') }}</th>
          <th>
            {{ $t('pages.companies.services.table.header.updateAt') }}
            <span
              class="patternList__sort"
              :class="sortStyle"
              @click="sortTable"
            />
          </th>
          <th>{{ $t('pages.companies.services.table.header.stores') }}</th>
          <th />
        </tr>
      </thead>
      <tbody class="patternList__tbody">
        <tr v-for="pattern in sortedPatterns" :key="pattern.id">
          <td class="patternList__name">
            {{ pattern.name }}
          </td>
          <td class="patternList__tag">
            <ul class="patternList__tag__items">
              <li
                v-for="item in pattern.serviceItems"
                :key="item.id"
                class="patternList__tag__item"
              >
                <KkTag :label="item.name" />
              </li>
            </ul>
          </td>
          <td class="patternList__date">
            {{ basicDateTimeFormat(pattern.updatedAt) }}
          </td>
          <td class="patternList__shops">
            <p class="patternList__shops__description">
              {{ $t('pages.companies.services.table.body.storeUsed') }}
              <component
                :is="pattern.storeCount ? 'button' : 'span'"
                :type="pattern.storeCount ? 'button' : null"
                :class="pattern.storeCount ? 'patternList__shops__count' : null"
                @click="showStores(pattern.id, pattern.storeCount)"
              >
                {{ $t('pages.companies.services.table.body.count', { count: pattern.storeCount }) }}
              </component>
            </p>
            <KkStatusLabel
              v-if="pattern.storeCount"
              :status="statusColor(pattern.progressCount, pattern.errorCount)"
              :text="statusLabel(pattern.progressCount, pattern.errorCount)"
              :sync-text-color="statusColor(pattern.progressCount, pattern.errorCount) === 2"
            />
          </td>
          <td class="patternList__action">
            <router-link
              :to="{
                name: 'CompaniesServicesEdit',
                params: { company_uuid: companyUuid, category_id: categoryId, id: pattern.id }
              }"
              class="patternList__action__item"
            >
              <span class="patternList__action__icon">
                <KkIcon name="edit" color="rgba(58, 59, 63, 0.7)" />
              </span>
              <span class="patternList__action__label">
                {{ $t('labels.edit') }}
              </span>
            </router-link>
            <router-link
              :to="{
                name: 'CompaniesServicesNew',
                params: { company_uuid: companyUuid, category_id: categoryId },
                query: { service_pattern_id: pattern.id }
              }"
              class="patternList__action__item"
            >
              <span class="patternList__action__icon">
                <KkIcon name="copy" color="rgba(58, 59, 63, 0.7)" />
              </span>
              <span class="patternList__action__label">
                {{ $t('labels.copy') }}
              </span>
            </router-link>
            <button
              type="button"
              class="patternList__action__item"
              @click="onDelete(pattern.id)"
            >
              <span class="patternList__action__icon">
                <KkIcon name="delete" color="rgba(58, 59, 63, 0.7)" />
              </span>
              <span class="patternList__action__label">
                {{ $t('labels.delete') }}
              </span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <KkModal :is-visible="isStores" width="720px" @change="hideStores">
      <KkBox :title="$t('pages.companies.services.stores.title')" content-height="64vh">
        <template #content>
          <KkLoading v-if="storeLoading" />
          <div v-else>
            <ul class="patternList__stores">
              <li
                v-for="store in stores"
                :key="store.uuid"
                class="patternList__store"
                :class="store.status === 'error' ? 'is-error' : null"
              >
                <span class="patternList__store__name">{{ store.name }}</span>
                <span v-if="store.status === 'error'" class="patternList__store__error">
                  {{ $t('pages.companies.services.stores.error') }}
                </span>
              </li>
            </ul>
          </div>
        </template>
        <template #footer>
          <div class="patternList__button">
            <KkButton color="secondary" :text="$t('labels.close')" @click="hideStores" />
          </div>
        </template>
      </KkBox>
    </KkModal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import cloneDeep from 'lodash.clonedeep'
import moment from 'moment'
import { KkBox, KkButton, KkIcon, KkStatusLabel, KkTag, KkModal, KkLoading } from '../../../base'
export default {
  name: 'KkPatternList',
  components: {
    KkBox,
    KkButton,
    KkIcon,
    KkStatusLabel,
    KkTag,
    KkModal,
    KkLoading,
  },
  props: {
    patterns: {
      type: Array,
      default: () => [],
      required: true,
    },
    categoryId: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      isStores: false,
      sort: 'desc',
    }
  },
  computed: {
    ...mapState({
      stores: state => state.companiesServicePatterns.stores,
      storeLoading: state => state.companiesServicePatterns.storeLoading,
    }),
    sortStyle() {
      if (this.sort === 'desc') {
        return 'is-desc'
      } else if (this.sort === 'asc') {
        return 'is-asc'
      } else {
        return null
      }
    },
    sortedPatterns() {
      const items = cloneDeep(this.patterns)
      if (this.sort === 'asc') {
        items.sort((i, j) => {
          if (moment(i.updatedAt).unix() < moment(j.updatedAt).unix()) return -1
          if (moment(i.updatedAt).unix() > moment(j.updatedAt).unix()) return 1
          return 0
        })
      } else if (this.sort === 'desc') {
        items.sort((i, j) => {
          if (moment(i.updatedAt).unix() > moment(j.updatedAt).unix()) return -1
          if (moment(i.updatedAt).unix() < moment(j.updatedAt).unix()) return 1
          return 0
        })
      }
      return items
    },
    companyUuid() {
      return this.$route.params.company_uuid
    },
  },
  methods: {
    ...mapActions({
      deleteServicePattern: 'companiesServicePatterns/deleteServicePattern',
      readServicePatternStores: 'companiesServicePatterns/readServicePatternStores',
    }),
    onDelete(id) {
      if (confirm(this.$t('pages.companies.services.confirm'))) {
        this.deleteServicePattern(id)
      }
    },
    basicDateTimeFormat(date) {
      return moment(date).format(this.$t('format.date.default'))
    },
    showStores(id, count) {
      if (!count) return
      this.readServicePatternStores(id)
      this.isStores = true
    },
    hideStores() {
      this.isStores = false
    },
    statusColor(progressCount, errorCount) {
      if (progressCount > 0) {
        return 3
      } else if (errorCount > 0) {
        return 2
      } else {
        return 1
      }
    },
    statusLabel(progressCount, errorCount) {
      if (progressCount > 0) {
        return this.$t('pages.companies.services.table.body.status.progress')
      } else if (errorCount > 0) {
        return this.$t('pages.companies.services.table.body.status.error', { count: errorCount })
      } else {
        return this.$t('pages.companies.services.table.body.status.complete')
      }
    },
    sortTable() {
      if (this.sort === 'asc' || this.sort === null) {
        this.sort = 'desc'
      } else if (this.sort === 'desc') {
        this.sort = 'asc'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';
@import '../../../../../../stylesheets/biz_spa/foundation/_mixin.scss';
.patternList {
  overflow-x: auto;
  margin: -8px 0;
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 4px;
    background-color: rgba($color-accent-primary, .15);
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: $color-accent-primary;
  }
  &__container {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 8px;
  }
  &__thead {
    th {
      padding: 16px;
      background-color: #ECEDF0;
      font-weight: bold;
      font-size: 12px;
      text-align: left;
      white-space: nowrap;
      &:first-child {
        padding-left: 24px;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
      &:last-child {
        padding-right: 32px;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }
  }
  &__tbody {
    td {
      padding: 16px;
      border-top: 1px solid #E4E7EB;
      border-bottom: 1px solid #E4E7EB;
      &:first-child {
        padding-left: 24px;
        border-left: 1px solid #E4E7EB;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
      &:last-child {
        padding-right: 32px;
        border-right: 1px solid #E4E7EB;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }
  }
  &__name {
    font-weight: bold;
    font-size: 14px;
  }
  &__tag {
    width: 30%;
    &__items {
      display: flex;
      flex-wrap: wrap;
       margin-right: -8px;
      margin-bottom: -8px;
    }
    &__item {
      margin-right: 8px;
      margin-bottom: 8px;
    }
  }
  &__date {
    font-size: 14px;
  }
  &__shops {
    white-space: nowrap;
    &__description {
      margin-bottom: 8px;
      font-size: 12px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    &__count {
      color: #E9446A;
      font-weight: bold;
      text-decoration: underline;
    }
  }
  &__action {
    &__item {
      display: flex;
      align-items: center;
      text-decoration: none;
      &:not(:first-child) {
        margin-top: 18px;
      }
    }
    &__icon {
      width: 16px;
    }
    &__label {
      margin-left: 10px;
      font-size: 12px;
      white-space: nowrap;
    }
  }
  &__sort {
    position: relative;
    padding-right: 20px;
    cursor: pointer;
    transition: .2s;
    &::before,
    &::after {
      position: absolute;
      top: 50%;
      right: 0;
      width: 0;
      height: 0;
      border-width: 0 4px 4px;
      border-style: solid;
      border-color: transparent transparent $color-font-base;
      content: "";
      pointer-events: none;
    }
    &::before {
      transform: translateY(-130%);
    }
    &::after {
      transform: translateY(30%) rotate(180deg);
    }
    &.is-desc {
      &::before {
        border-color: transparent transparent $color-accent-disable;
      }
      &::after {
        border-color: transparent transparent $color-accent-primary;
      }
    }
    &.is-asc {
      &::before {
        border-color: transparent transparent $color-accent-primary;
      }
      &::after {
        border-color: transparent transparent $color-accent-disable;
      }
    }
  }
  &__button {
    display: flex;
    justify-content: flex-end;
  }
  &__store {
    display: flex;
    font-size: 14px;
    line-height: 1.72;
    &:not(:first-child) {
      margin-top: 16px;
    }
    &__name {
      max-width: 188px;
    }
    &__error {
      margin-left: 24px;
    }
    &.is-error {
      color: $color-accent-primary;
    }
  }
}
</style>
